import { Link } from "react-router-dom";
import { ArrowUpRight, Lock } from "lucide-react";
import { TOPIC } from "@/constants/testIds";
import { renderWithRedactions } from "@/components/TextFormatter";
import { API, BACKEND_URL } from "@/lib/api";
import { displayTitleForTopic } from "@/lib/factAlignment";

const CATEGORY_LABEL = {
        politica: "Política",
        media: "Media",
        justica: "Justiça",
        economico: "Económico",
        social: "Social",
        historico: "Histórico",
        ciencia_saude: "Ciência e Saúde",
        religiao: "Religião",
        esoterismo: "Esoterismo",
};

const CATEGORY_CODE = {
        politica: "POL",
        media: "MED",
        justica: "JUS",
        economico: "ECO",
        social: "SOC",
        historico: "HIS",
        ciencia_saude: "CIE",
        religiao: "REL",
        esoterismo: "ESO",
};

export const TopicCard = ({ topic, index = 0 }) => {
        const score = topic.controversy_score ?? 0;
        const ref = String(index + 1).padStart(3, "0");
        const displayTitle = displayTitleForTopic(topic);
        return (
                <Link
                        to={`/topico/${topic.slug}`}
                        data-testid={TOPIC.card(topic.slug)}
                        className="dossier-card group flex flex-col p-8 min-h-[340px]"
                        style={{ animationDelay: `${index * 70}ms` }}
                >
                        <div className="file-tab">
                                {CATEGORY_CODE[topic.category]} / {ref}
                        </div>
                        <div className="flex items-start justify-between gap-4 mt-2">
                                <div className="flex items-center gap-3">
                                        <Lock
                                                size={12}
                                                strokeWidth={1.5}
                                                className="text-[var(--ink-faint)] group-hover:text-[var(--controversy)] transition-colors"
                                        />
                                        <span className="overline text-[var(--ink-soft)]">
                                                {CATEGORY_LABEL[topic.category] || topic.category}
                                        </span>
                                </div>
                                <ArrowUpRight
                                        size={20}
                                        strokeWidth={1.5}
                                        className="opacity-20 group-hover:opacity-100 group-hover:text-[var(--controversy)] transition-all"
                                />
                        </div>

                        {topic.image_url && (
                                <div className="w-full h-36 mt-6 mb-2 relative overflow-hidden bg-[var(--line-faint)] border border-[var(--line-strong)]">
                                        <div className="absolute inset-0 bg-black/5 z-10 group-hover:bg-transparent transition-colors duration-300 pointer-events-none" />
                                        <img 
                                                src={topic.image_url.startsWith('/uploads/') ? `${BACKEND_URL}${topic.image_url}` : topic.image_url} 
                                                alt={displayTitle} 
                                                className="w-full h-full object-cover filter grayscale sepia-[.2] contrast-125 opacity-90 group-hover:opacity-100 transition-opacity"
                                        />
                                </div>
                        )}

                        <h3
                                data-testid={TOPIC.cardTitle(topic.slug)}
                                className={`font-serif text-2xl md:text-[28px] leading-[1.1] ${topic.image_url ? 'mt-4' : 'mt-8'} tracking-tight text-[var(--ink)] group-hover:text-[var(--ink)]`}
                        >
                                {renderWithRedactions(displayTitle)}
                        </h3>

                        <p className="text-[13px] leading-relaxed text-[var(--ink-soft)] mt-5 line-clamp-3 flex-1 font-mono">
                                {renderWithRedactions(topic.summary)}
                        </p>

                        <div className="mt-8 pt-4 border-t border-[var(--line)]">
                                <div className="flex items-center justify-between text-[10px] font-mono tracking-widest uppercase text-[var(--ink-faint)] mb-2">
                                        <span>Índice de controvérsia</span>
                                        <span style={{ color: score > 70 ? "var(--deception)" : score > 40 ? "var(--controversy)" : "var(--truth)" }}>
                                                {String(score).padStart(2, "0")}/100
                                        </span>
                                </div>
                                <div className="score-bar">
                                        <span style={{ width: `${score}%` }} />
                                </div>
                        </div>

                        {topic.tags?.length > 0 && (
                                <div className="mt-4 flex flex-wrap gap-1">
                                        {topic.tags.slice(0, 3).map((t) => (
                                                <span
                                                        key={t}
                                                        className="border border-[var(--line-strong)] px-2 py-[2px] text-[9.5px] uppercase font-mono tracking-[0.18em] text-[var(--ink-soft)]"
                                                >
                                                        {t}
                                                </span>
                                        ))}
                                </div>
                        )}
                </Link>
        );
};

export default TopicCard;
